import React, { useState, useRef, useEffect } from 'react';
import { Category } from '../../types';
import '../../styles/CategoryDropdown.css';

interface CategoryDropdownProps {
  categories: Category[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  title?: string;
  onKeyPress?: (e: React.KeyboardEvent) => void;
}

const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
  categories,
  value,
  onChange,
  placeholder = "Select category...",
  disabled = false,
  className = "",
  title = "Select category",
  onKeyPress
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const listboxId = `category-listbox-${Math.random().toString(36).substr(2, 9)}`;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
    
    onKeyPress?.(e);
  };

  const handleOptionClick = (categoryId: string) => {
    onChange(categoryId);
    setIsOpen(false);
  };

  const handleOptionMouseEnter = (categoryId: string, event: React.MouseEvent) => {
    setHoveredOption(categoryId);
    
    // Position tooltip
    const option = event.currentTarget as HTMLElement;
    const tooltip = tooltipRef.current;
    if (tooltip) {
      const rect = option.getBoundingClientRect();
      tooltip.style.left = `${rect.right + 10}px`;
      tooltip.style.top = `${rect.top}px`;
    }
  };

  const handleOptionMouseLeave = () => {
    setHoveredOption(null);
  };

  const selectedCategory = categories.find(cat => cat.id.toString() === value);

  return (
    <div 
      ref={dropdownRef}
      className={`category-dropdown ${className} ${disabled ? 'disabled' : ''}`}
      title={title}
    >
      <div 
        className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
      >
        <span className="dropdown-value">
          {selectedCategory ? selectedCategory.name : placeholder}
        </span>
        <i className={`fas fa-chevron-down dropdown-arrow ${isOpen ? 'rotated' : ''}`}></i>
      </div>

      {isOpen && (
        <div className="dropdown-options" role="listbox" id={listboxId}>
          <div 
            className="dropdown-option"
            onClick={() => handleOptionClick('')}
            role="option"
            aria-selected={value === ''}
          >
            <span className="option-text">{placeholder}</span>
          </div>
          {categories.map(category => (
            <div
              key={category.id}
              className={`dropdown-option ${value === category.id.toString() ? 'selected' : ''}`}
              onClick={() => handleOptionClick(category.id.toString())}
              onMouseEnter={(e) => handleOptionMouseEnter(category.id.toString(), e)}
              onMouseLeave={handleOptionMouseLeave}
              role="option"
              aria-selected={value === category.id.toString()}
            >
              <span className="option-text">{category.name}</span>
              {category.description && (
                <i className="fas fa-info-circle option-info-icon" title="Has description"></i>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tooltip */}
      {hoveredOption && (
        <div 
          ref={tooltipRef}
          className="category-tooltip"
          style={{ position: 'fixed', zIndex: 9999 }}
        >
          {(() => {
            const category = categories.find(cat => cat.id.toString() === hoveredOption);
            if (category?.description) {
              return (
                <div className="tooltip-content">
                  <div className="tooltip-title">{category.name}</div>
                  <div className="tooltip-description">{category.description}</div>
                  <div className="tooltip-meta">
                    <span className={`tooltip-type ${category.type}`}>
                      {category.type}
                    </span>
                    <span className={`tooltip-account ${category.account_type}`}>
                      {category.account_type}
                    </span>
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}
    </div>
  );
};

export default CategoryDropdown; 